import { useState } from 'react';
import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import Input from '../Input/Input';
import type { NumberInputProps } from './types/NumberInputProps';
import { formatValue } from '../helpers/formatValue';

const NumberInput = ({
  className = '',
  currency = false,
  dob = false,
  phone = false,
  ssn = false,
  onChange,
  ...props
}: NumberInputProps) => {
  const [value, setValue] = useState(() => {
    const valueAsString = props.value ? String(props.value) : '';

    if (isNonEmptyString(valueAsString)) {
      return formatValue(valueAsString, currency, phone, ssn, dob);
    }

    return '';
  });

  const handleKeyDown = (event: KeyboardEvent) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Enter',
    ];

    if (
      !allowedKeys.includes(event.key) && // Allow control keys
      !event.ctrlKey && // Allow Ctrl shortcuts (ex. Ctrl+V paste)
      !event.metaKey && // Allow Cmd shortcuts (ex. Cmd+V paste on macOS)
      (isNaN(Number(event.key)) || event.key === ' ') // Block non-numeric keys and space
    ) {
      event.preventDefault();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatValue(
      event.currentTarget.value,
      currency,
      phone,
      ssn,
      dob,
    );

    setValue(formattedValue);

    // Call the parent onChange event if provided, with the formatted value
    // so consumers receive the formatted string rather than the raw input
    if (onChange) {
      event.currentTarget.value = formattedValue;
      onChange(event);
    }
  };

  const inputType = phone ? 'tel' : 'text';

  return (
    <Input
      {...props}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      type={inputType}
      inputMode="numeric"
      pattern={phone ? undefined : '[0-9]*'}
      className={className}
    />
  );
};

export default NumberInput;
